import { useMemo } from "react";
import { useLayerSelector } from "../store/layer";
import { useMapSelector } from "../store/map";
import { useSelectedEntity } from "./useSelectedEntity";

/**
 * Derives the set of layer IDs that should actually be drawn on the map right
 * now, given the user's explicit per-layer toggles and the "show only
 * {endpoint} results on map" filter from the data-grid toolbar.
 *
 * The filter flag lives on the map store and historically was consumed only in
 * `Layers.tsx` via a local memo — which meant the layers panel never learnt
 * about the filter and its checkboxes stayed "on" while the map hid layers.
 * Hoisting the derivation into a shared hook gives `Layers.tsx` (what the map
 * draws) and `LayersPanel.tsx` (what the user sees toggled) a single source of
 * truth, and exposes enough metadata for the panel to render dimmed rows with
 * an escape-hatch click handler that clears the filter.
 *
 * `effectiveVisibleLayers` always returns the same Set identity when inputs
 * are stable (useMemo), so downstream `Set.has(...)` callers won't thrash.
 */
export interface UseEffectiveVisibleLayersResult {
  /** Layer IDs that should render as "visible" on the map right now. */
  effectiveVisibleLayers: Set<string>;
  /** True iff the endpoint-filter is active AND we know which layer to scope to. */
  isFilteredByEndpoint: boolean;
  /** The layer id the endpoint filter is pinned to (null when not filtering). */
  endpointLayerId: string | null;
}

export const useEffectiveVisibleLayers = (): UseEffectiveVisibleLayersResult => {
  const { visibleLayers } = useLayerSelector();
  const { showOnlySelectedEndpointsDataOnMap } = useMapSelector();
  const { endpoint } = useSelectedEntity();
  const endpointLayerId = endpoint?.mapLayerId ?? null;

  return useMemo(() => {
    const isFilteredByEndpoint = Boolean(showOnlySelectedEndpointsDataOnMap && endpointLayerId);

    // 1. Not filtering: Show all standard visible layers + ALL dynamic CM Table layers
    if (!isFilteredByEndpoint) {
      const effectiveSet = new Set(visibleLayers);
      
      return {
        effectiveVisibleLayers: effectiveSet,
        isFilteredByEndpoint: false,
        endpointLayerId
      };
    }

    // 2. Filtering: Show ONLY the selected endpoint's layer
    const narrowed = new Set<string>();
    if (endpointLayerId && visibleLayers.has(endpointLayerId)) {
      narrowed.add(endpointLayerId);
    }

    return {
      effectiveVisibleLayers: narrowed,
      isFilteredByEndpoint: true,
      endpointLayerId
    };
  }, [visibleLayers, showOnlySelectedEndpointsDataOnMap, endpointLayerId]);
};
